// Legal doc check: what the Legal upload box knows about the files picked in
// it, shared with the submit buttons (create ticket, comment) so they can ask
// "submit anyway?" and link the checks to what was saved.
//
// Nothing here can stop a submit. If anything fails, the box says so and the
// upload carries on exactly as it does for everyone else.

import { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import Swal from 'sweetalert2';

const cookies = new Cookies();
const API = () => process.env.REACT_APP_PATH_DESTINATION + '/legalCheck';
const config = () => ({ headers: { 'x-access-token': cookies.get('sessionToken', { path: '/' }) } });

export const CHECKED_EXT = ['.docx', '.doc', '.pdf'];
const POLL_MS = 4000;

// ---- is this a Legal account? asked once per token --------------------------
// The answer is kept per token, so only the first upload box of a session waits
// for it. A failed request is not kept: the next box asks again.
const enabledFor = new Map();   // token -> true | false
let pending = null;             // { token, promise }

export function legalEnabled() {
  const token = cookies.get('sessionToken', { path: '/' });
  if (!token) return Promise.resolve(false);
  if (enabledFor.has(token)) return Promise.resolve(enabledFor.get(token));
  if (!pending || pending.token !== token) {
    pending = {
      token,
      promise: axios.get(API() + '/enabled', config())
        .then((r) => {
          const v = !!(r.data && r.data.data && r.data.data.enabled);
          enabledFor.set(token, v);
          return v;
        })
        .catch(() => false)
        .finally(() => { if (pending && pending.token === token) pending = null; }),
    };
  }
  return pending.promise;
}

// null while it is not known yet, so the caller can hold the upload box back
// instead of showing the plain one and swapping it under a file being dropped.
export function useLegalEnabled(active = true) {
  const token = cookies.get('sessionToken', { path: '/' });
  const known = active && token && enabledFor.has(token) ? enabledFor.get(token) : null;
  const [enabled, setEnabled] = useState(active ? known : false);
  useEffect(() => {
    let live = true;
    if (active) legalEnabled().then((v) => { if (live) setEnabled(v); });
    else setEnabled(false);
    return () => { live = false; };
  }, [active, token]);
  return active ? enabled : false;
}

// ---- the files in each box -------------------------------------------------------
// entry: { uid, file, name, checkable, state: checking|ready|error|skipped, data, error,
//          seq (which check request is current), challenges (clicks not yet confirmed) }
const entries = { tiket: [], komen: [] };
const listeners = { tiket: new Set(), komen: new Set() };
let uidSeq = 0;

const notify = (source) => listeners[source].forEach((fn) => fn([...entries[source]]));

export function useLegalEntries(source) {
  const [list, setList] = useState([...entries[source]]);
  useEffect(() => {
    listeners[source].add(setList);
    setList([...entries[source]]);
    return () => listeners[source].delete(setList);
  }, [source]);
  return list;
}

const extOf = (name) => {
  const i = String(name || '').lastIndexOf('.');
  return i < 0 ? '' : String(name).slice(i).toLowerCase();
};

const update = (source, uid, patch) => {
  entries[source] = entries[source].map((e) => (e.uid === uid ? { ...e, ...patch } : e));
  notify(source);
};
const find = (source, uid) => entries[source].find((e) => e.uid === uid);

// Set the challenged flag of one finding, wherever it is listed.
const withChallenge = (d, key, value) => {
  const mark = (st) => (st && st.findings
    ? { ...st, findings: st.findings.map((f) => (f.key === key ? { ...f, challenged: value } : f)) } : st);
  return { ...d, rules: mark(d.rules), ai: mark(d.ai), translation: mark(d.translation) };
};

// Server data for an entry, with any challenge click the server has not
// confirmed yet laid over it, so a poll landing mid-click cannot undo the click.
// A poll that left out the layout (the clauses' text; the page said it already
// had that version) keeps the one it has.
const applyServer = (source, uid, data) => {
  const e = find(source, uid);
  if (!e) return;
  let d = data;
  if (d && d.layout === undefined && e.data && e.data.id === d.id && e.data.layout_key === d.layout_key) {
    d = { ...d, layout: e.data.layout };
  }
  Object.entries(e.challenges || {}).forEach(([key, value]) => { d = withChallenge(d, key, value); });
  update(source, uid, { data: d });
};

// One poll loop per file at most, however many things asked for one.
const polling = new Set();
function poll(source, uid) {
  const tag = `${source}:${uid}`;
  if (polling.has(tag)) return;
  polling.add(tag);
  const step = () => {
    const e = find(source, uid);
    if (!e || !e.data || !e.data.running) { polling.delete(tag); return; }
    const id = e.data.id;
    const have = e.data.layout ? `?have=${encodeURIComponent(e.data.layout_key || '')}` : '';
    axios.get(`${API()}/${id}${have}`, config())
      .then((r) => {
        const now = find(source, uid);
        // Ignore an answer about a check this file has since been rechecked away from.
        if (r.data && r.data.status === '200' && now && now.data && now.data.id === id) {
          applyServer(source, uid, r.data.data);
        }
      })
      .catch(() => {})
      .finally(() => {
        const now = find(source, uid);
        if (now && now.data && now.data.running) setTimeout(step, POLL_MS);
        else polling.delete(tag);
      });
  };
  setTimeout(step, POLL_MS);
}

function runCheck(source, uid, recheck) {
  const e = find(source, uid);
  if (!e) return;
  const seq = (e.seq || 0) + 1;
  update(source, uid, { state: 'checking', error: null, seq, challenges: {} });
  const form = new FormData();
  form.append('file', e.file);
  form.append('source', source);
  if (recheck) form.append('recheck', '1');
  const current = () => { const now = find(source, uid); return now && now.seq === seq; };
  const lateTag = `${source}:${uid}:${seq}`;
  axios.post(API(), form, config())
    .then((r) => {
      const d = r.data && r.data.data;
      if (r.data && r.data.status === '200' && d && d.id) remember(lateTag, d);
      // Submitted while this check was still running: link it now it is back.
      if (late.has(lateTag)) {
        const target = late.get(lateTag);
        late.delete(lateTag);
        if (r.data && r.data.status === '200' && d && d.id) {
          postAttach(target, [{ id: d.id, high: highOf(d), ai_running: !!d.running }]);
        }
      }
      if (!current()) return;
      if (r.data && r.data.status === '200' && d && d.checked === false) {
        update(source, uid, { state: 'skipped', data: null });
      } else if (r.data && r.data.status === '200' && d) {
        update(source, uid, { state: 'ready', data: d });
        if (d.running) poll(source, uid);
      } else {
        update(source, uid, { state: 'error', error: (r.data && r.data.message) || 'Checker unavailable' });
      }
    })
    .catch(() => {
      late.delete(lateTag);
      if (current()) update(source, uid, { state: 'error', error: 'Checker unavailable' });
    });
}

export function addFile(source, file) {
  if (entries[source].some((e) => e.file === file)) return;
  const checkable = CHECKED_EXT.includes(extOf(file.name));
  const uid = ++uidSeq;
  entries[source] = [...entries[source], {
    uid, file, name: file.name, checkable, state: checkable ? 'checking' : 'skipped',
    data: null, error: null, seq: 0, challenges: {},
  }];
  notify(source);
  if (checkable) runCheck(source, uid, false);
}

export function removeFile(source, file) {
  // The check stays in the database: an abandoned upload is kept on purpose.
  entries[source] = entries[source].filter((e) => e.file !== file);
  notify(source);
}

export function recheck(source, uid) { runCheck(source, uid, true); }

export function requestTranslation(source, uid) {
  const e = find(source, uid);
  if (!e || !e.data) return;
  const id = e.data.id;
  const form = new FormData();
  form.append('file', e.file);
  const setStatus = (status) => {
    const now = find(source, uid);
    if (now && now.data && now.data.id === id) {
      update(source, uid, { data: { ...now.data, translation: { ...now.data.translation, status } } });
    }
  };
  update(source, uid, { data: { ...e.data, translation: { ...e.data.translation, status: 'queued' }, running: true } });
  axios.post(`${API()}/${id}/translation`, form, config())
    .then((r) => {
      const now = find(source, uid);
      if (!now || !now.data || now.data.id !== id) return;
      if (r.data && r.data.status === '200') { applyServer(source, uid, r.data.data); poll(source, uid); }
      else setStatus('failed');
    })
    .catch(() => setStatus('unavailable'));
}

export function toggleChallenge(source, uid, finding) {
  const e = find(source, uid);
  if (!e || !e.data) return;
  const id = e.data.id;
  const on = !finding.challenged;
  // Shown at once and remembered until the server answers; put back only if
  // the server did not take it.
  update(source, uid, {
    data: withChallenge(e.data, finding.key, on),
    challenges: { ...(e.challenges || {}), [finding.key]: on },
  });
  const settle = (ok) => {
    const now = find(source, uid);
    if (!now || !now.data || now.data.id !== id) return;
    const challenges = { ...(now.challenges || {}) };
    if (challenges[finding.key] === on) delete challenges[finding.key];
    update(source, uid, { challenges, data: ok ? now.data : withChallenge(now.data, finding.key, !on) });
  };
  axios.post(`${API()}/${id}/challenge`, { key: finding.key, on }, config())
    .then((r) => settle(!!(r.data && r.data.status === '200')))
    .catch(() => settle(false));
}

// ---- at submit ----------------------------------------------------------------------

// HIGH findings still standing: one Legal has challenged is not counted.
function highOf(d) {
  return [d.rules, d.ai, d.translation]
    .reduce((n, st) => n + ((st && st.findings) || []).filter((f) => f.severity === 'HIGH' && !f.challenged).length, 0);
}

// What is in the box: each finished check by id, and each check still running
// by the request it is waiting on (a Recheck in flight counts as running, so
// the check it replaces is not the one linked).
const snapshotOf = (source) => entries[source]
  .map((e) => {
    if (e.state === 'checking') return { pending: true, uid: e.uid, seq: e.seq };
    if (e.data && e.data.id) return { id: e.data.id, high: highOf(e.data), ai_running: !!e.data.running };
    return null;
  })
  .filter(Boolean);

// What was in the box when Legal pressed submit. Kept apart from the live list,
// because a comment box can close (and clear the list) before the save answers.
const submitted = { tiket: null, komen: null };
// Checks still running when their ticket or comment was saved, by request:
// linked when they come back.
const late = new Map();
// The last few check requests that came back, by request, so a submit can
// still link one whose box was cleared in the meantime.
const finished = new Map();
function remember(tag, d) {
  finished.set(tag, { id: d.id, high: highOf(d), ai_running: !!d.running });
  if (finished.size > 100) finished.delete(finished.keys().next().value);
}

function postAttach(target, checks) {
  const body = { ...target, checks };
  if (!checks.length && !target.from_draft_id) return;
  axios.post(API() + '/attach', body, config()).catch(() => {});
}

// The machine check still reading a file (a long scan, in the background).
const READING = ['pending', 'queued', 'running'];

// Resolves true to go ahead. Only asks when there is something worth a second
// look: HIGH findings showing, a file still being read, or the AI still
// working. Never blocks.
export async function legalConfirm(source) {
  submitted[source] = null;
  if (!entries[source].length) return true;
  let high = 0;
  let reading = false;
  let running = false;
  // The same file twice in one box is one document: count its HIGH once.
  const counted = new Set();
  entries[source].forEach((e) => {
    if (e.state === 'checking' || (e.data && e.data.rules && READING.includes(e.data.rules.status))) reading = true;
    else if (e.data && e.data.running) running = true;
    if (e.data) {
      const fp = e.data.sha || `id${e.data.id}`;
      if (!counted.has(fp)) { counted.add(fp); high += highOf(e.data); }
    }
  });
  if (high || reading || running) {
    const parts = [];
    if (high) parts.push(`${high} HIGH finding${high === 1 ? '' : 's'}`);
    if (reading) parts.push('a file is still being checked');
    if (running) parts.push('the AI is still working');
    const said = parts.join(', ');
    const r = await Swal.fire({
      title: 'Submit anyway?',
      text: `${said.charAt(0).toUpperCase()}${said.slice(1)}. You can still submit.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Submit anyway',
      cancelButtonText: 'Go back',
      focusCancel: true,
      reverseButtons: true,
    });
    if (!r.isConfirmed) return false;
  }
  submitted[source] = snapshotOf(source);
  return true;
}

// After the ticket, comment or draft was saved: link each check to it, then
// forget them here. Legal does not see these results again; they stay in the
// database. A check still running is linked when it comes back.
//   ticket:  { tiket_id, from_draft_id (when the ticket was a draft) }
//   comment: { tiket_id, komen_id }
//   draft:   { draft_id, from_draft_id (when an older draft was saved again) }
export function legalAttach(source, target = {}) {
  const snap = submitted[source] || snapshotOf(source);
  submitted[source] = null;
  const ready = [];
  snap.forEach((c) => {
    if (!c.pending) { ready.push(c); return; }
    const tag = `${source}:${c.uid}:${c.seq}`;
    if (finished.has(tag)) ready.push(finished.get(tag));   // came back since submit
    else late.set(tag, target);                               // still running: link on return
  });
  entries[source] = [];
  notify(source);
  postAttach(target, ready);
}

// When the box is thrown away without a submit (page left, dropzone reset).
// What was captured at submit is left alone for legalAttach.
export function legalReset(source) {
  entries[source] = [];
  notify(source);
}
